import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { accountingApi } from '../services/api';
import {
  FileText, RefreshCw, Plus, Trash2, BookOpen, Scale, ScrollText, Receipt,
  Landmark, Loader2, ArrowDownToLine,
} from 'lucide-react';

type Tab = 'chart' | 'journal' | 'balance' | 'ledger' | 'vat';

const TYPE_LABEL: Record<string, string> = {
  asset: 'Activo',
  liability: 'Passivo',
  equity: 'Capital Próprio',
  revenue: 'Rendimentos',
  expense: 'Gastos',
};

export const Accounting: React.FC = () => {
  const [tab, setTab] = useState<Tab>('chart');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));

  // Plano de contas
  const [chart, setChart] = useState<any[]>([]);
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ code: '', name: '', type: 'asset', nature: 'debit' });

  // Diário
  const [journal, setJournal] = useState<any[]>([]);
  const [journalSummary, setJournalSummary] = useState({ debits: 0, credits: 0 });

  // Balancete
  const [balanceSheet, setBalanceSheet] = useState<any[]>([]);
  const [balanceTotals, setBalanceTotals] = useState({ totalDebits: 0, totalCredits: 0, balanced: true, entryCount: 0 });

  // Razão
  const [ledger, setLedger] = useState<any>(null);
  const [ledgerAccount, setLedgerAccount] = useState('4100');

  // IVA
  const [vat, setVat] = useState<any>(null);

  const loadChart = useCallback(async () => {
    try {
      const data = await accountingApi.getChart();
      setChart(data.accounts || []);
    } catch (err) {
      toast.error('Erro ao carregar o plano de contas');
    }
  }, []);

  const loadJournal = useCallback(async () => {
    setLoading(true);
    try {
      const data = await accountingApi.getJournal({ startDate, endDate });
      setJournal(data.entries || []);
      setJournalSummary(data.summary || { debits: 0, credits: 0 });
    } catch (err) {
      toast.error('Erro ao carregar o diário');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const loadBalance = useCallback(async () => {
    setLoading(true);
    try {
      const data = await accountingApi.getBalanceSheet({ startDate, endDate });
      setBalanceSheet(data.rows || []);
      setBalanceTotals({ totalDebits: data.totalDebits, totalCredits: data.totalCredits, balanced: data.balanced, entryCount: data.entryCount });
    } catch (err) {
      toast.error('Erro ao gerar o balancete');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const loadLedger = useCallback(async () => {
    setLoading(true);
    try {
      const data = await accountingApi.getLedger({ account: ledgerAccount, startDate, endDate });
      setLedger(data);
    } catch (err) {
      toast.error('Erro ao carregar o razão');
    } finally {
      setLoading(false);
    }
  }, [ledgerAccount, startDate, endDate]);

  const loadVat = useCallback(async () => {
    setLoading(true);
    try {
      const data = await accountingApi.getVat({ startDate, endDate });
      setVat(data);
    } catch (err) {
      toast.error('Erro ao apurar o IVA');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === 'journal') loadJournal();
    else if (tab === 'balance') loadBalance();
    else if (tab === 'ledger') loadLedger();
    else if (tab === 'vat') loadVat();
  }, [tab, loadJournal, loadBalance, loadLedger, loadVat]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await accountingApi.sync({ startDate, endDate });
      toast.success(res.message || 'Diário sincronizado');
      if (tab === 'journal') loadJournal();
      else if (tab === 'balance') loadBalance();
      else if (tab === 'ledger') loadLedger();
      else if (tab === 'vat') loadVat();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.code || !newAccount.name) {
      toast.error('Código e nome são obrigatórios');
      return;
    }
    try {
      await accountingApi.createChartAccount(newAccount);
      toast.success('Conta criada');
      setShowNewAccount(false);
      setNewAccount({ code: '', name: '', type: 'asset', nature: 'debit' });
      loadChart();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao criar conta');
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Eliminar esta conta do plano?')) return;
    try {
      await accountingApi.deleteChartAccount(id);
      toast.success('Conta eliminada');
      loadChart();
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao eliminar conta');
    }
  };

  const fmt = (v: number) => (v || 0).toLocaleString('pt-MZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('pt-MZ') : '-';

  const tabs: { id: Tab; label: string; icon: any }[] = [
    { id: 'chart', label: 'Plano de Contas', icon: BookOpen },
    { id: 'journal', label: 'Diário', icon: ScrollText },
    { id: 'balance', label: 'Balancete', icon: Scale },
    { id: 'ledger', label: 'Razão', icon: Receipt },
    { id: 'vat', label: 'Apuramento IVA', icon: Landmark },
  ];

  const exportCsv = (filename: string, header: string[], rows: any[][]) => {
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const content = [header.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportBalance = () => {
    exportCsv(`balancete_${startDate}_${endDate}.csv`,
      ['Conta', 'Nome', 'Tipo', 'Débito', 'Crédito', 'Saldo'],
      balanceSheet.map((r) => [r.code, r.name, TYPE_LABEL[r.type] || r.type, fmt(r.debits), fmt(r.credits), fmt(r.debits - r.credits)]));
  };

  const handleExportJournal = () => {
    const rows: any[][] = [];
    journal.forEach((e) => {
      e.lines.forEach((l: any) => {
        rows.push([fmtDate(e.date), e.description, e.sourceType, l.accountCode, l.accountName, fmt(l.debit), fmt(l.credit)]);
      });
    });
    exportCsv(`diario_${startDate}_${endDate}.csv`,
      ['Data', 'Descrição', 'Fonte', 'Conta', 'Nome', 'Débito', 'Crédito'], rows);
  };

  const handleExportLedger = () => {
    if (!ledger) return;
    exportCsv(`razao_${ledger.account.code}_${startDate}_${endDate}.csv`,
      ['Data', 'Descrição', 'Débito', 'Crédito', 'Saldo'],
      ledger.lines.map((l: any) => [fmtDate(l.date), l.description, fmt(l.debit), fmt(l.credit), fmt(l.balance)]));
  };

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Contabilista</h1>
          <p className="text-gray-500 mt-1">Contabilidade básica (Nível 2) &amp; Compliance Moçambique (Nível 3)</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm" />
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg px-4 py-2 text-sm font-semibold"
          >
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Sincronizar Diário
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              tab === t.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* ─────────── Plano de Contas ─────────── */}
      {tab === 'chart' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Plano de Contas (SNC simplificado)</h2>
            <button
              onClick={() => setShowNewAccount(!showNewAccount)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 text-sm font-semibold"
            >
              <Plus size={16} /> Nova Conta
            </button>
          </div>

          {showNewAccount && (
            <div className="p-5 border-b border-gray-100 bg-gray-50">
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                <input value={newAccount.code} onChange={(e) => setNewAccount({ ...newAccount, code: e.target.value })}
                  placeholder="Código (ex: 1400)" className="border rounded-lg px-3 py-2 text-sm" />
                <input value={newAccount.name} onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                  placeholder="Nome da conta" className="border rounded-lg px-3 py-2 text-sm sm:col-span-2" />
                <select value={newAccount.type} onChange={(e) => setNewAccount({ ...newAccount, type: e.target.value })}
                  className="border rounded-lg px-3 py-2 text-sm">
                  {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={newAccount.nature} onChange={(e) => setNewAccount({ ...newAccount, nature: e.target.value })}
                  className="border rounded-lg px-3 py-2 text-sm">
                  <option value="debit">Débito</option>
                  <option value="credit">Crédito</option>
                </select>
              </div>
              <div className="mt-3">
                <button onClick={handleCreateAccount} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-semibold">
                  Criar conta
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-3 font-semibold">Código</th>
                  <th className="px-5 py-3 font-semibold">Nome</th>
                  <th className="px-5 py-3 font-semibold">Tipo</th>
                  <th className="px-5 py-3 font-semibold">Natureza</th>
                  <th className="px-5 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {chart.map((a) => (
                  <tr key={a._id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono font-semibold text-blue-700">{a.code}</td>
                    <td className="px-5 py-3">{a.name}</td>
                    <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">{TYPE_LABEL[a.type] || a.type}</span></td>
                    <td className="px-5 py-3 text-gray-600">{a.nature === 'debit' ? 'Débito' : 'Crédito'}</td>
                    <td className="px-5 py-3 text-right">
                      {!a.isSystem && (
                        <button onClick={() => handleDeleteAccount(a._id)} className="text-red-500 hover:text-red-700">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─────────── Diário ─────────── */}
      {tab === 'journal' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Diário — lançamentos ({journal.length})</h2>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-600">Débito: <b className="text-blue-700">{fmt(journalSummary.debits)}</b></span>
              <span className="text-gray-600">Crédito: <b className="text-green-700">{fmt(journalSummary.credits)}</b></span>
              <button onClick={handleExportJournal} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold">
                <ArrowDownToLine size={16} /> CSV
              </button>
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={24} /></div>
          ) : journal.length === 0 ? (
            <div className="p-10 text-center text-gray-400">
              <FileText className="inline mb-2" size={32} />
              <p>Sem lançamentos no período. Clica em "Sincronizar Diário" para gerar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-3 font-semibold">Data</th>
                    <th className="px-5 py-3 font-semibold">Descrição</th>
                    <th className="px-5 py-3 font-semibold">Fonte</th>
                    <th className="px-5 py-3 font-semibold">Conta</th>
                    <th className="px-5 py-3 font-semibold">Débito</th>
                    <th className="px-5 py-3 font-semibold">Crédito</th>
                  </tr>
                </thead>
                <tbody>
                  {journal.map((e) => (
                    <React.Fragment key={e._id}>
                      {e.lines.map((l: any, idx: number) => (
                        <tr key={`${e._id}-${idx}`} className="border-b border-gray-50 hover:bg-gray-50">
                          {idx === 0 && (
                            <>
                              <td className="px-5 py-2" rowSpan={e.lines.length}>{fmtDate(e.date)}</td>
                              <td className="px-5 py-2" rowSpan={e.lines.length}>{e.description}<div className="text-xs text-gray-400">{e.sourceType}</div></td>
                            </>
                          )}
                          <td className="px-5 py-2 font-mono text-blue-700">{l.accountCode}</td>
                          <td className="px-5 py-2 text-gray-600">{l.accountName}</td>
                          <td className="px-5 py-2 text-blue-700">{l.debit ? fmt(l.debit) : ''}</td>
                          <td className="px-5 py-2 text-green-700">{l.credit ? fmt(l.credit) : ''}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────────── Balancete ─────────── */}
      {tab === 'balance' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Balancete</h2>
              <p className={`text-xs font-semibold ${balanceTotals.balanced ? 'text-green-600' : 'text-red-600'}`}>
                {balanceTotals.balanced ? '✓ Equilibrado (débito = crédito)' : '✗ Desequilibrado'} • {balanceTotals.entryCount} lançamentos
              </p>
            </div>
            <button onClick={handleExportBalance} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold text-sm">
              <ArrowDownToLine size={16} /> Exportar CSV
            </button>
          </div>
          {loading ? (
            <div className="p-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={24} /></div>
          ) : balanceSheet.length === 0 ? (
            <div className="p-10 text-center text-gray-400"><p>Sem movimentos no período.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-3 font-semibold">Conta</th>
                    <th className="px-5 py-3 font-semibold">Nome</th>
                    <th className="px-5 py-3 font-semibold">Tipo</th>
                    <th className="px-5 py-3 text-right font-semibold">Débito</th>
                    <th className="px-5 py-3 text-right font-semibold">Crédito</th>
                    <th className="px-5 py-3 text-right font-semibold">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {balanceSheet.map((r) => (
                    <tr key={r.code} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-5 py-2.5 font-mono font-semibold text-blue-700">{r.code}</td>
                      <td className="px-5 py-2.5">{r.name}</td>
                      <td className="px-5 py-2.5"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">{TYPE_LABEL[r.type] || r.type}</span></td>
                      <td className="px-5 py-2.5 text-right text-blue-700">{fmt(r.debits)}</td>
                      <td className="px-5 py-2.5 text-right text-green-700">{fmt(r.credits)}</td>
                      <td className="px-5 py-2.5 text-right font-semibold">{fmt(r.debits - r.credits)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-bold">
                    <td className="px-5 py-3" colSpan={3}>TOTAL</td>
                    <td className="px-5 py-3 text-right text-blue-700">{fmt(balanceTotals.totalDebits)}</td>
                    <td className="px-5 py-3 text-right text-green-700">{fmt(balanceTotals.totalCredits)}</td>
                    <td className="px-5 py-3 text-right">{fmt(balanceTotals.totalDebits - balanceTotals.totalCredits)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────────── Razão ─────────── */}
      {tab === 'ledger' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-gray-900">Razão</h2>
              <select value={ledgerAccount} onChange={(e) => setLedgerAccount(e.target.value)}
                className="border rounded-lg px-3 py-1.5 text-sm">
                {chart.map((a) => <option key={a._id} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-4 text-sm">
              {ledger && (
                <>
                  <span className="text-gray-600">Saldo final: <b>{fmt(ledger.closingBalance)}</b></span>
                  <button onClick={handleExportLedger} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold">
                    <ArrowDownToLine size={16} /> CSV
                  </button>
                </>
              )}
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-gray-400"><Loader2 className="animate-spin inline" size={24} /></div>
          ) : !ledger || ledger.lines.length === 0 ? (
            <div className="p-10 text-center text-gray-400"><p>Sem movimentos para esta conta no período.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-3 font-semibold">Data</th>
                    <th className="px-5 py-3 font-semibold">Descrição</th>
                    <th className="px-5 py-3 text-right font-semibold">Débito</th>
                    <th className="px-5 py-3 text-right font-semibold">Crédito</th>
                    <th className="px-5 py-3 text-right font-semibold">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.lines.map((l: any, i: number) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-5 py-2.5">{fmtDate(l.date)}</td>
                      <td className="px-5 py-2.5">{l.description}</td>
                      <td className="px-5 py-2.5 text-right text-blue-700">{l.debit ? fmt(l.debit) : ''}</td>
                      <td className="px-5 py-2.5 text-right text-green-700">{l.credit ? fmt(l.credit) : ''}</td>
                      <td className="px-5 py-2.5 text-right font-semibold">{fmt(l.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────────── Apuramento IVA ─────────── */}
      {tab === 'vat' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="p-10 text-center text-gray-400 col-span-3"><Loader2 className="animate-spin inline" size={24} /></div>
          ) : vat && (
            <>
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-1">Apuramento de IVA</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Período: {vat.period?.startDate} → {vat.period?.endDate}
                  {vat.company?.name ? ` • ${vat.company.name}${vat.company.taxId ? ` (NIF ${vat.company.taxId})` : ''}` : ''}
                </p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center bg-blue-50 rounded-xl px-4 py-3">
                    <span className="text-sm font-semibold text-blue-800">IVA Liquidado (vendas) — {vat.company?.taxRate ?? ''}%</span>
                    <span className="font-bold text-blue-800">{fmt(vat.vatLiquidado)} MT</span>
                  </div>
                  <div className="flex justify-between items-center bg-gray-50 rounded-xl px-4 py-3">
                    <span className="text-sm font-semibold text-gray-700">IVA Dedutível (compras/despesas)</span>
                    <span className="font-bold text-gray-700">-{fmt(vat.vatDedutivel)} MT</span>
                  </div>

                  <div className="rounded-xl border border-gray-100 divide-y divide-gray-50">
                    <div className="flex justify-between items-center px-4 py-2.5 bg-gray-50 rounded-t-xl">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Desagregação por regime</span>
                      <span className="text-[10px] font-bold text-gray-400">base tributável</span>
                    </div>
                    {['normal', 'isento', 'simplificado'].map((mode) => {
                      const r = vat.regimes?.[mode] || { base: 0, tax: 0, rate: 0 };
                      const labels: Record<string, string> = {
                        normal: `Vendas a ${vat.company?.taxRate || 16}%`,
                        isento: 'Vendas isentas (art. 9.º CIVA)',
                        simplificado: 'Regime simplificado (ISPC)',
                      };
                      return (
                        <div key={mode} className="flex justify-between items-center px-4 py-2">
                          <span className="text-sm text-gray-700">{labels[mode]}</span>
                          <span className="text-sm font-bold text-gray-800">{fmt(r.base)} MT</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className={`flex justify-between items-center rounded-xl px-4 py-4 ${vat.status === 'zero' ? 'bg-gray-50' : vat.vatAPagar > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                    <span className="text-base font-bold">{vat.status === 'a_recuperar' ? 'IVA a Recuperar' : 'IVA a Pagar'}</span>
                    <span className="text-xl font-black">{fmt(Math.abs(vat.vatAPagar))} MT</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-2 text-sm text-gray-500">
                    <span>Base tributável do período</span>
                    <span>{fmt(vat.taxableBase)} MT</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Nível 3 — Compliance</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Relatórios prontos para o contabilista, exportáveis em CSV/Excel (export PDF disponível na Demonstração de Resultados).
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center gap-2"><Scale size={14} className="text-blue-600" /> Balancete do período</li>
                  <li className="flex items-center gap-2"><Receipt size={14} className="text-blue-600" /> Razão por conta</li>
                  <li className="flex items-center gap-2"><Landmark size={14} className="text-blue-600" /> Declaração de IVA (apuramento)</li>
                </ul>
                <p className="text-xs text-gray-400 mt-4">
                  Os export CSV são gerados no separador respectivo (Diário, Balancete, Razão). Os valores alimentam directamente o diário e a demonstração de resultados.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};